import React, { useState, useEffect } from 'react';
import { Play, RotateCcw, FileText, Upload, Image } from 'lucide-react';
import MaskAnnotator from '@/components/MaskAnnotationDialog';

// Create test mask data (white circle on transparent background)
const createTestMask = (width = 800, height = 600) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  // Clear canvas (transparent)
  ctx.clearRect(0, 0, width, height);
  
  // Draw white circle for mask
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(width / 2, height / 2, Math.min(width, height) / 4, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw some additional mask areas
  ctx.beginPath();
  ctx.arc(width / 3, height / 3, 50, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.beginPath();
  ctx.arc(width * 2/3, height * 2/3, 30, 0, Math.PI * 2);
  ctx.fill();
  
  return canvas.toDataURL('image/png');
};

// Generate solid color test images
const createTestImage = (color = '#4f46e5', width = 800, height = 600, text = '') => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  // Create gradient background
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, adjustBrightness(color, -30));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  // Add some geometric patterns
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 10; i++) {
    ctx.beginPath();
    ctx.moveTo(i * width / 10, 0);
    ctx.lineTo(i * width / 10, height);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(0, i * height / 10);
    ctx.lineTo(width, i * height / 10);
    ctx.stroke();
  }
  
  // Add text
  if (text) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = 'bold 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2);
  }
  
  return canvas.toDataURL('image/png');
};

// Helper to adjust color brightness
const adjustBrightness = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255))
    .toString(16).slice(1);
};

export default function MaskAnnotatorDemo() {
  const [testImages, setTestImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [testData, setTestData] = useState({
    review_lock_id: 'lock_12345',
    review_id: 'review_67890'
  });

  // Initialize test images on mount
  useEffect(() => {
    const images = [
      {
        id: 1,
        name: 'Test Product 1',
        url: createTestImage('#4f46e5', 800, 600, 'Sample 1'),
        maskUrl: null, // No initial mask
        tag: 'defect',
        color: '#ef4444'
      },
      {
        id: 2,
        name: 'Test Product 2',
        url: createTestImage('#10b981', 800, 600, 'Sample 2'),
        maskUrl: createTestMask(800, 600), // Has initial mask
        tag: 'scratch',
        color: '#f59e0b'
      },
      {
        id: 3,
        name: 'Test Product 3',
        url: createTestImage('#ec4899', 800, 600, 'Sample 3'),
        maskUrl: null,
        tag: 'damage',
        color: '#8b5cf6'
      }
    ];
    setTestImages(images);
  }, []);

  const handleStartReview = (image) => {
    setSelectedImage(image);
    setReviewResult(null);
  };

  const handleReviewComplete = (result) => {
    console.log('Review completed:', result);
    setReviewResult(result);
  };

  const handleReset = () => {
    setSelectedImage(null);
    setReviewResult(null);
    setUploadedImage(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImage = {
          id: Date.now(),
          name: file.name,
          url: event.target.result,
          maskUrl: null,
          tag: 'custom',
          color: '#6366f1'
        };
        setUploadedImage(newImage);
        setTestImages(prev => [...prev.filter(img => img.id !== newImage.id), newImage]);
      };
      reader.readAsDataURL(file);
    }
  };

  // If reviewing an image, show the annotator
  if (selectedImage && !reviewResult) {
    return (
      <div className="h-screen flex flex-col">
        <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-400">Reviewing:</span>
            <span className="ml-2 font-medium">{selectedImage.name}</span>
          </div>
          <button
            onClick={handleReset}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm"
          >
            Cancel Review
          </button>
        </div>
        <div className="flex-1">
          <MaskAnnotator
            imageUrl={selectedImage.url}
            maskData={selectedImage.maskUrl ? { mask: selectedImage.maskUrl, tag: selectedImage.tag, comment: '' } : null}
            tag={selectedImage.tag}
            tagColor={selectedImage.color}
            review_lock_id={testData.review_lock_id}
            review_id={testData.review_id}
            onComplete={handleReviewComplete}
            containerMode={true}
          />
        </div>
      </div>
    );
  }

  // Show result if review is complete
  if (reviewResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-4xl w-full bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-3 rounded-full ${
              reviewResult.status === 'accepted' ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <FileText className={`w-6 h-6 ${
                reviewResult.status === 'accepted' ? 'text-green-600' : 'text-red-600'
              }`} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Review Complete</h2>
              <p className="text-gray-600">
                Image {reviewResult.status === 'accepted' ? 'accepted' : 'rejected'}
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Result Data</h3>
              <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-96">
{JSON.stringify(reviewResult, null, 2)}
              </pre>
            </div>

            {reviewResult.masks_data?.mask && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Generated Mask Preview</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Original Image</p>
                    <img 
                      src={selectedImage.url} 
                      alt="Original" 
                      className="w-full rounded border"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Mask Output</p>
                    <div className="relative bg-black rounded border">
                      <img 
                        src={reviewResult.masks_data.mask} 
                        alt="Mask" 
                        className="w-full opacity-90"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {reviewResult.comment && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Comment</h3>
                <p className="text-sm text-blue-800">{reviewResult.comment}</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Review Another Image
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show image selection screen
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mask Annotator Demo
          </h1>
          <p className="text-gray-600">
            Select an image to start reviewing and editing masks
          </p>
        </div>

        {/* Test Data Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Configuration</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Lock ID
              </label>
              <input
                type="text"
                value={testData.review_lock_id}
                onChange={(e) => setTestData({ ...testData, review_lock_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review ID
              </label>
              <input
                type="text"
                value={testData.review_id}
                onChange={(e) => setTestData({ ...testData, review_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Upload Custom Image */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Custom Image</h2>
          <div className="flex items-center gap-4">
            <label className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">Click to upload an image</p>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
              </div>
            </label>
            {uploadedImage && (
              <div className="text-sm text-green-600 flex items-center gap-2">
                <Image className="w-4 h-4" />
                <span>Custom image loaded!</span>
              </div>
            )}
          </div>
        </div>

        {/* Image Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testImages.map((image) => (
            <div
              key={image.id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="aspect-video bg-gray-200 relative overflow-hidden">
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                {image.maskUrl && (
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded">
                    Has Mask
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{image.name}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-gray-600">Tag:</span>
                  <span
                    className="px-2 py-1 text-xs font-medium text-white rounded"
                    style={{ backgroundColor: image.color }}
                  >
                    {image.tag}
                  </span>
                </div>
                <button
                  onClick={() => handleStartReview(image)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Start Review
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">How to Use</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Select an image or upload your own to start the review process</li>
            <li>• Images with "Has Mask" badge come with pre-existing masks to edit</li>
            <li>• Use the brush tool to draw or edit masks</li>
            <li>• Accept if mask is correct, Reject if you made changes</li>
            <li>• Review results will be displayed after completion</li>
          </ul>
          
          <h3 className="font-semibold text-blue-900 mb-2 mt-4">Controls</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Draw:</strong> Left click and drag</li>
            <li>• <strong>Pan:</strong> Middle mouse, Space+drag, or Shift+drag</li>
            <li>• <strong>Zoom:</strong> Ctrl+Scroll (at cursor) or zoom buttons</li>
            <li>• <strong>Brush Size:</strong> Scroll wheel</li>
            <li>• <strong>Toggle Eraser:</strong> Press X key</li>
          </ul>
        </div>
      </div>
    </div>
  );
}